import { memo, useEffect, useRef } from 'react';
import Box from '../Box/Box';
import { ProgressBarProps } from './ProgressBar.types';

import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import useStyle from '../../hooks/useStyle';

function ProgressBar({
  from,
  to,
  progress,
  level = 0,
  animationDuration = 500,
  gradientType = 'darkSurfaceBrand',
  renderPercent,
  onLayout,
  progressGlowColor,
  ...rest
}: ProgressBarProps) {
  const percent = Math.min((progress - from) / (to - from), 1);
  const progressWidth = useSharedValue(percent * 100);
  const prevLevel = useSharedValue(level);
  const glowOpacity = useSharedValue(0);
  const prevProgress = useRef<number | undefined>(undefined);

  // TODO: Add Box Shadow

  useEffect(() => {
    if (level && level !== prevLevel.value && level > prevLevel.value) {
      // Fill the bar fully, then reset to 0 , then fill to the new level
      progressWidth.value = withSequence(
        withTiming(100, { duration: animationDuration }), // fill full width
        withTiming(0, { duration: 0 }), // reset to 0
        withTiming(percent * 100, { duration: animationDuration }), // fill to new level
      );
      prevLevel.value = level;
    } else {
      progressWidth.value = withTiming(percent * 100, {
        duration: animationDuration,
      });
    }
  }, [progress, level]);

  useEffect(() => {
    if (prevProgress.current !== undefined && progress > prevProgress.current) {
      glowOpacity.value = withSequence(
        withTiming(1, {
          duration: animationDuration,
          easing: Easing.linear,
        }),
        withTiming(0, {
          duration: animationDuration,
          easing: Easing.linear,
        }),
      );
    }

    prevProgress.current = progress;
  }, [progress]);

  useEffect(() => {
    if (prevLevel.value > level) {
      prevLevel.value = level;
    }
  }, [level]);

  const animatedStyle = useAnimatedStyle(() => ({
    left: '-100%',
    transform: [{ translateX: `${progressWidth.value}%` }],
  }));

  const glowAnimatedStyle = useAnimatedStyle(() => ({
    opacity: glowOpacity.value,
  }));

  const { color: rawGlowColor } = useStyle({ color: progressGlowColor });

  return (
    <Box position="relative" {...rest}>
      {progressGlowColor ? (
        <Box
          position="absolute"
          top="space-0"
          bottom="space-0"
          left="space-0"
          right="space-0"
          boxShadow={`0 0 12px ${rawGlowColor}`}
          animatedStyle={glowAnimatedStyle}
          borderRadius="radius-full"
        />
      ) : null}
      <Box
        width="100%"
        height={10}
        borderRadius="radius-full"
        backgroundColor="screenBackground" // in figma it says bg-default. how to determine which color to use?
        overflow="hidden"
        onLayout={onLayout}
      >
        <Box
          height="100%"
          width="100%"
          borderRadius="radius-full"
          variant="gradient"
          gradient={gradientType}
          animatedStyle={animatedStyle}
          position="relative"
        >
          {renderPercent ? renderPercent(Math.floor(percent * 100)) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(ProgressBar);
